export const useCurrency = () => {
  const { isLoggedIn } = useAuth()

  const { isLoggedInMerchantGroup } = useMerchantGroupAuth()

  /**
   * Formats a minor unit value using the currency's precision, prefixing GBP/EUR
   * symbols and suffixing other codes, with optional compact and zero-stripped output.
   */
  const formatToCurrencyString = (
    price: number,
    currencyCode: string,
    {
      compact = false,
      stripTrailingZeros = false,
    }: {
      compact?: boolean
      stripTrailingZeros?: boolean
    } = {}
  ): string => {
    const localeBcp47 = "en-GB"
    const normalizedCurrencyCode =
      currencyCode.toUpperCase()
    const currencyFractionDigits =
      new Intl.NumberFormat(localeBcp47, {
        style: "currency",
        currency: normalizedCurrencyCode,
      }).resolvedOptions().maximumFractionDigits ?? 2
    const amountInMajorUnits =
      price / 10 ** currencyFractionDigits
    const maximumFractionDigits =
      stripTrailingZeros &&
      Number.isInteger(amountInMajorUnits)
        ? 0
        : currencyFractionDigits
    const formattedAmount = new Intl.NumberFormat(
      localeBcp47,
      {
        maximumFractionDigits,
        minimumFractionDigits: maximumFractionDigits,
        ...(compact && {
          notation: "compact",
        }),
      }
    ).format(amountInMajorUnits)

    const currencySymbol =
      normalizedCurrencyCode === "GBP"
        ? "£"
        : normalizedCurrencyCode === "EUR"
          ? "€"
          : undefined

    if (!currencySymbol) {
      return `${formattedAmount} ${normalizedCurrencyCode}`
    }

    return formattedAmount.startsWith("-")
      ? `-${currencySymbol}${formattedAmount.slice(1)}`
      : `${currencySymbol}${formattedAmount}`
  }

  const formatToMerchantCurrencyString = (
    price: number,
    {
      compact = false,
      stripTrailingZeros = false,
    }: {
      compact?: boolean
      stripTrailingZeros?: boolean
    } = {}
  ): string => {
    if (!isLoggedIn.value) {
      throw new Error(
        "formatToMerchantCurrencyString may only be called when logged in as a merchant"
      )
    }

    const { currency } = useTenant()

    return formatToCurrencyString(price, currency.value, {
      compact,
      stripTrailingZeros,
    })
  }

  const formatToMerchantGroupCurrencyString = (
    price: number,
    {
      compact = false,
      stripTrailingZeros = false,
    }: {
      compact?: boolean
      stripTrailingZeros?: boolean
    } = {}
  ): string => {
    if (!isLoggedInMerchantGroup.value) {
      throw new Error(
        "formatToMerchantGroupCurrencyString may only be called when logged in as a merchant group"
      )
    }

    const merchantGroup = useMerchantGroup()

    return formatToCurrencyString(
      price,
      merchantGroup.value?.currency ?? "GBP",
      {
        compact,
        stripTrailingZeros,
      }
    )
  }

  const getCurrencySymbol = (currencyCode: string) =>
    currencyCode === "GBP"
      ? "£"
      : currencyCode
        ? "€"
        : currencyCode

  return {
    formatToMerchantCurrencyString,
    getCurrencySymbol,
    formatToCurrencyString,
    formatToMerchantGroupCurrencyString,
  }
}
